// ReCaptcha Hook for Frictionless Signup
// Integrates Google ReCaptcha v3 for bot protection

import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

/**
 * Custom hook for ReCaptcha v3 integration
 *
 * @returns Object containing getRecaptchaToken function
 *
 * @example
 * ```typescript
 * const { getRecaptchaToken } = useRecaptcha();
 *
 * // In form submit handler
 * const handleSubmit = async (data) => {
 *   try {
 *     const token = await getRecaptchaToken('signup');
 *     await submitSignup({ ...data, recaptcha_token: token });
 *   } catch (error) {
 *     console.error('ReCaptcha failed:', error);
 *   }
 * };
 * ```
 */
export const useRecaptcha = (): { getRecaptchaToken: (action?: string) => Promise<string> } => {
  const { executeRecaptcha } = useGoogleReCaptcha();

  /**
   * Get ReCaptcha token for verification
   *
   * @param action - Action name for ReCaptcha (default: 'signup')
   * @returns Promise resolving to ReCaptcha token string
   * @throws Error if ReCaptcha is not initialized or execution fails
   */
  const getRecaptchaToken = useCallback(
    async (action: string = 'signup'): Promise<string> => {
      if (executeRecaptcha == null) {
        console.error('ReCaptcha not initialized');
        throw new Error('ReCaptcha not available');
      }

      try {
        const token = await executeRecaptcha(action);
        return token;
      } catch (error) {
        console.error('ReCaptcha execution failed:', error);
        throw new Error('Failed to verify you are not a robot');
      }
    },
    [executeRecaptcha],
  );

  return { getRecaptchaToken };
};

/**
 * HOW TO USE THIS HOOK:
 *
 * 1. Ensure component is wrapped in GoogleReCaptchaProvider (already done in index.tsx for signup route)
 *
 * 2. Import and use the hook in your component:
 *    ```typescript
 *    import { useRecaptcha } from '../hooks/useRecaptcha';
 *
 *    const MyComponent = () => {
 *      const { getRecaptchaToken } = useRecaptcha();
 *      // ... use in form submission
 *    };
 *    ```
 *
 * 3. Call getRecaptchaToken before submitting sensitive forms:
 *    ```typescript
 *    const onSubmit = async (formData) => {
 *      const token = await getRecaptchaToken('signup');
 *      await api.signup({ ...formData, recaptcha_token: token });
 *    };
 *    ```
 *
 * NOTES:
 * - ReCaptcha v3 runs in the background and doesn't require user interaction
 * - The 'action' parameter helps Google analyze the context (e.g., 'signup', 'login')
 * - Tokens are single-use and expire after a few minutes
 * - Backend must validate the token with Google's API
 */
